import {
  dashboardMetricWidgetSchema,
  dashboardPerformanceChartWidgetSchema,
  dashboardPublisherWidgetSchema,
  dashboardDemandIntegrationsWidgetSchema,
  dashboardDemandPartnersWidgetSchema,
  dashboardNetworkWidgetSchema,
  dashboardSrpmRankingWidgetSchema,
  DashboardWidgetType,
} from './model';
import type { DashboardDateRange, DashboardWidget } from './model';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ComponentType } from 'react';
import type { DashboardOverview } from './model';
import {
  ChartRenderer,
  MetricRenderer,
  TableRenderer,
  type DashboardWidgetRendererProps,
} from './widgetRenderers';
import type {
  dashboardCanvasRendererProps,
  dashboardWidgetRegistry as dashboardWidgetRegistryContract,
} from '@/components/dashboard/DashboardCanvas';
import { DashboardWidgetRenderer } from './components/DashboardWidgetRenderer';

interface dashboardWidgetDefinition {
  schema: { safeParse: (value: unknown) => { success: boolean } };
  component: ComponentType<DashboardWidgetRendererProps>;
}

export const dashboardWidgetRegistry: Record<DashboardWidgetType, dashboardWidgetDefinition> = {
  [DashboardWidgetType.Metric]: { schema: dashboardMetricWidgetSchema, component: MetricRenderer },
  [DashboardWidgetType.PerformanceChart]: {
    schema: dashboardPerformanceChartWidgetSchema,
    component: ChartRenderer,
  },
  [DashboardWidgetType.PublisherPerformance]: {
    schema: dashboardPublisherWidgetSchema,
    component: TableRenderer,
  },
  [DashboardWidgetType.DemandIntegrations]: {
    schema: dashboardDemandIntegrationsWidgetSchema,
    component: TableRenderer,
  },
  [DashboardWidgetType.DemandPartners]: {
    schema: dashboardDemandPartnersWidgetSchema,
    component: TableRenderer,
  },
  [DashboardWidgetType.SrpmRanking]: {
    schema: dashboardSrpmRankingWidgetSchema,
    component: TableRenderer,
  },
  [DashboardWidgetType.NetworkPerformance]: {
    schema: dashboardNetworkWidgetSchema,
    component: TableRenderer,
  },
};

export interface MainDashboardRuntime {
  rangesByWidgetId: ReadonlyMap<string, DashboardDateRange>;
  queriesByRangeKey: ReadonlyMap<string, UseQueryResult<DashboardOverview> | undefined>;
  rangeKey: (range: DashboardDateRange) => string;
}

export const mainDashboardCanvasRegistry: dashboardWidgetRegistryContract<
  DashboardWidget,
  MainDashboardRuntime
> = Object.fromEntries(
  Object.entries(dashboardWidgetRegistry).map(([type, definition]) => {
    function MainDashboardCanvasRenderer({
      widget,
      runtime,
    }: dashboardCanvasRendererProps<DashboardWidget, MainDashboardRuntime>) {
      const range = runtime.rangesByWidgetId.get(widget.id);
      if (!range) return null;
      return (
        <DashboardWidgetRenderer
          widget={widget}
          range={range}
          query={runtime.queriesByRangeKey.get(runtime.rangeKey(range))}
          withLayout={false}
        />
      );
    }
    return [type, { schema: definition.schema, component: MainDashboardCanvasRenderer }];
  }),
);

export type { DashboardWidgetRendererProps } from './widgetRenderers';
